import { Chain, ChainPath } from '@ankr.com/chains-list';

import { hasAllowedPaths } from './hasAllowedPaths';
import { clearPathPrefix } from './clearPathPrefix';

interface FilterChainsByPathsParams {
  chain: Chain;
  paths: ChainPath[];
}

type FilterChainByPathsFN = (params: FilterChainsByPathsParams) => Chain;

const filterSubChainsByPaths = (
  chains: Chain[] = [],
  paths: ChainPath[],
  mapper: FilterChainByPathsFN,
) =>
  chains
    .filter(chain => {
      const hasPaths = Boolean(chain.paths && chain.paths.length > 0);

      return Boolean(!hasPaths || hasAllowedPaths(chain, paths));
    })
    .map(chain => mapper({ chain, paths }));

// shark-proxy grants <path>_vrpc to every key allowed on <path>
const withVrpcPaths = (paths: ChainPath[]) => [
  ...paths,
  ...paths.map(path => `${path}_vrpc` as ChainPath),
];

export const filterChainByPaths: FilterChainByPathsFN = ({ chain, paths }) => {
  const newPaths = chain.paths?.filter(path =>
    paths.includes(clearPathPrefix(path)),
  );

  return {
    ...chain,
    paths: newPaths,
    beacons: filterSubChainsByPaths(chain.beacons, paths, filterChainByPaths),
    beaconsTestnet: filterSubChainsByPaths(
      chain.testnets?.flatMap(({ beacons = [] }) => beacons),
      paths,
      filterChainByPaths,
    ),
    devnets: filterSubChainsByPaths(chain.devnets, paths, filterChainByPaths),
    extenders: filterSubChainsByPaths(
      chain.extenders,
      paths,
      filterChainByPaths,
    ),
    extensions: filterSubChainsByPaths(
      chain.extensions,
      paths,
      filterChainByPaths,
    ),
    opnodes: filterSubChainsByPaths(chain.opnodes, paths, filterChainByPaths),
    opnodesTestnet: filterSubChainsByPaths(
      chain.testnets?.flatMap(({ opnodes = [] }) => opnodes),
      paths,
      filterChainByPaths,
    ),
    testnets: filterSubChainsByPaths(chain.testnets, paths, filterChainByPaths),
    signets: filterSubChainsByPaths(chain.signets, paths, filterChainByPaths),
    vrpcs: filterSubChainsByPaths(
      chain.vrpcs,
      withVrpcPaths(paths),
      filterChainByPaths,
    ),
    urls: newPaths?.length ? chain.urls : [],
  };
};
